import {HttpClient} from '@angular/common/http';
import {Injectable, computed, inject, signal} from '@angular/core';
import {Observable, catchError, finalize, of, shareReplay, tap} from 'rxjs';
import {
    MapLayer,
    MapLayerCategory,
    MapLayerGroup,
} from 'src/app/shared/interfaces/map-layer-catalog.interface';

/** Current state of the shared active map-layer catalog. */
export type MapLayerCatalogStatus = 'idle' | 'loading' | 'ready' | 'error';

/** Options used when loading the active layer catalog. */
export interface MapLayerCatalogLoadOptions {
    /** Starts a new request after a completed load instead of returning the current cached value. */
    force?: boolean;
}

/** Loads and indexes the active layer catalog for every map owner in the authenticated application. */
@Injectable({providedIn: 'root'})
export class MapLayerCatalogService {
    private readonly http = inject(HttpClient);
    private readonly categoriesState = signal<MapLayerCategory[]>([]);
    private readonly statusState = signal<MapLayerCatalogStatus>('idle');
    private readonly errorState = signal<unknown>(undefined);
    private inFlightRequest?: Observable<MapLayerCategory[]>;

    /** Sorted active categories shared by Mapper and embedded map owners. */
    public readonly categories = this.categoriesState.asReadonly();
    /** Current request state used by map loading and retry interfaces. */
    public readonly status = this.statusState.asReadonly();
    /** Last request failure. Existing cached categories remain available after a refresh failure. */
    public readonly error = this.errorState.asReadonly();
    private readonly layersByUiHandle = computed(() => {
        const layers = new Map<string, MapLayer>();

        this.categoriesState().forEach((category) => {
            category.groups?.forEach((group) => {
                group.layers?.forEach((layer) => layers.set(layer.uiHandle, layer));
            });
        });

        return layers;
    });

    /** Returns the active catalog layer with the exact stable `uiHandle`. */
    public getLayerByUiHandle(uiHandle: string): MapLayer | undefined {
        return this.layersByUiHandle().get(uiHandle);
    }

    /** Loads the active catalog once, deduplicating simultaneous app, Mapper, and widget requests. */
    public loadActiveCatalog(options: MapLayerCatalogLoadOptions = {}): Observable<MapLayerCategory[]> {
        if (this.inFlightRequest) {
            return this.inFlightRequest;
        }

        if (!options.force && this.statusState() === 'ready') {
            return of(this.categoriesState());
        }

        this.statusState.set('loading');
        this.errorState.set(undefined);

        const request = this.http.get<MapLayerCategory[]>('api/maplayer/categories?activeonly=true').pipe(
            tap((categories) => {
                this.categoriesState.update(() => this.sortCatalog(categories));
                this.statusState.set('ready');
            }),
            catchError((error: unknown) => {
                this.errorState.update(() => error);
                this.statusState.set('error');
                return of(this.categoriesState());
            }),
            finalize(() => {
                if (this.inFlightRequest === request) {
                    this.inFlightRequest = undefined;
                }
            }),
            shareReplay({bufferSize: 1, refCount: false}),
        );

        this.inFlightRequest = request;
        return request;
    }

    /** Retries the active catalog request while preserving any usable cached catalog. */
    public retry(): Observable<MapLayerCategory[]> {
        return this.loadActiveCatalog({force: true});
    }

    /** Sorts immutable copies so backend order wins and names provide a stable tie-breaker. */
    private sortCatalog(categories: MapLayerCategory[]): MapLayerCategory[] {
        return categories
            .toSorted((first, second) => this.compareCatalogItems(first, second))
            .map((category) => ({
                ...category,
                groups: (category.groups ?? [])
                    .toSorted((first, second) => this.compareCatalogItems(first, second))
                    .map((group) => ({
                        ...group,
                        layers: (group.layers ?? []).toSorted((first, second) =>
                            this.compareCatalogItems(first, second),
                        ),
                    })),
            }));
    }

    /** Applies the shared ordering rule at category, group, and layer levels. */
    private compareCatalogItems(
        first: Pick<MapLayerCategory | MapLayerGroup | MapLayer, 'displayName' | 'ordering'>,
        second: Pick<MapLayerCategory | MapLayerGroup | MapLayer, 'displayName' | 'ordering'>,
    ): number {
        const orderDifference = (first.ordering ?? 0) - (second.ordering ?? 0);

        return orderDifference || (first.displayName ?? '').localeCompare(second.displayName ?? '');
    }
}
